import { ClassFlag } from "../../../../library-js/dom-tools/ClassFlag";
import { ElementAttribute } from "../../../../library-js/dom-tools/ElementAttribute";
import { ElementType } from "../../../../library-js/dom-tools/ElementType";
import { OnClick } from "../../../../library-js/dom-tools/EventListeners";
import { HTMLEditor } from "../../../../library-js/dom-tools/HTMLEditor";
import { TreeWalker } from "../../../../library-js/graphs/TreeWalker";
import { Lexer } from "../../../../library-js/text/lexer/Lexer";
import { LexerMatcher } from "../../../../library-js/text/lexer/LexerMatcher";
import { LexerMatcherLibrary } from "../../../../library-js/text/lexer/LexerMatcherLibrary";
import { WordLexer } from "../../../../library-js/text/lexer/WordLexer";
import { RegExpUtility } from "../../../../library-js/text/RegExpUtility";
import { Rokojori } from "../../../js/Rokojori";
import { GodotIcons } from "../GodotIcons";
import { TextAlignment } from "../../../../library-js/text/aligner/TextAlignment";
import { GodotDocsIndexSelector } from "../../../js/godot-docs/GodotDocsIndexSelector";
import { GodotDocsFile } from "../../../js/godot-docs/GodotDocsFile";

export class GodotNodeWalker extends TreeWalker<GodotNode>
{  
  parent( node:GodotNode ):GodotNode  
  {
    return node.parent;
  }
    
  childAt( node:GodotNode, index:number ):GodotNode
  {
    return node.children[ index ];
  }
    
  numChildren( node:GodotNode ):number
  {
    return node.children.length;
  }
}

export class GodotNode
{
  static readonly walker = new GodotNodeWalker();

  static readonly type = new ElementType<HTMLElement>( "godot-node" );
  static readonly icon  = new ElementType<HTMLElement>( "godot-icon" );
  static readonly nodeIcon  = new ElementType<HTMLElement>( "godot-node-icon" );
  static readonly iconAttribute = new ElementAttribute( "icon" );
  static readonly label = new ElementType<HTMLElement>( "godot-label" );
  static readonly open = new ElementType<HTMLElement>( "godot-expander-open" );
  static readonly closed = new ElementType<HTMLElement>( "godot-expander-closed" );


  _offset:number = -2;
  _type:string;
  _icon:string;
  _name:string;
  _childrenVisible:boolean = true;

  _element:HTMLElement;
  _children:GodotNode[] = [];
  _parent:GodotNode;
  _iconElement:HTMLElement;
  _labelElement:HTMLElement;

  
  
  get parent(){ return this._parent;}
  get children(){ return this._children; }

  
  get label()
  {
    return RegExpUtility.isEmptyString( this._name ) ? this._type : this._name; 
  }

  get image()
  {
    return this._icon || this._type;
  }

  get element()
  {
    return this._element;
  }

  addChild( child:GodotNode )
  {
    this._children.push( child );
    child._parent = this;
  }

  isVisibleInParents()
  {
    let it = this.parent;

    while ( it )
    {
      if ( ! it._childrenVisible )
      {
        return false;
      }

      it = it.parent;
    }

    return true;
  }

  computeVisibility()
  {
    ClassFlag.hidden.setAs( this.element, ! this.isVisibleInParents() );    

    this.updateChildrenVisibility();
  }

  updateChildrenVisibility()
  {
    this.children.forEach( c => c.computeVisibility() );
  }

  toggleChildrenVisibility()
  { 
    if ( this.children.length <= 0 )
    {
      return;
    }

    this.setChildrenVisibility( ! this._childrenVisible );
  }

  setChildrenVisibility( visible:boolean )
  {    
    if ( this.children.length <= 0 )
    {
      return;
    }

    this._childrenVisible = visible;

    // console.log( this._childrenVisible );

    if ( this._childrenVisible )
    {
      ClassFlag.closed.removeFrom( this.element );
      ClassFlag.open.set( this.element );      
    }
    else
    {
      ClassFlag.open.removeFrom( this.element );
      ClassFlag.closed.set( this.element );
    }

    this.updateChildrenVisibility();
    
  }

  static readonly regex = /(^\s*)([^:]*)(?::\s*(.+))?/
  static readonly typeIconRegex = /(\w+)\s*\((.+?)\)\s*/;

  static root():GodotNode
  {
    let n = new GodotNode();
    n._offset = -2;
    n._name = "%ROOT";
    return n;

  }

  static create( line:string ):GodotNode
  {
    let i = new GodotNode();
    let match = GodotNode.regex.exec( line );
    
    if ( match == null )
    {
      console.log( "Couldn't match: ", `"${line}"` );
      i._offset = 0;
      i._type = "unknown";
      return i;
    }

    i._offset = match[ 1 ] == null ? 0 : match[ 1 ].length;
    i._type   = match[ 2 ];

    if ( GodotNode.typeIconRegex.test( i._type ) )
    {
      let typeIconMatch = GodotNode.typeIconRegex.exec( i._type );
      i._type = typeIconMatch[ 1 ];
      i._icon = typeIconMatch[ 2 ];
    }

    i._name   = match[ 3 ];


    let label = i.label;
    let aligner = new TextAlignment();
    aligner.align( label );
    aligner.harmonize();
    
    /*
    let lexer = new WordLexer();
    let words = lexer.lexTokens( i.label );
    let anySet = new Set<string>();
    anySet.add( LexerMatcherLibrary.ANY_SYMBOL_MATCHER.type ); 
    words = lexer.compress( words, anySet );
    lexer.resolveMatches( i.label, words );

    let label:string[] = [];

    let lineLength = 0;
    let maxChars = 40;
     
    for ( let i = 0; i < words.length; i++ )
    {
      if ( words[ i ].isDoneOrError )
      {
        continue;
      }

      let willOverflow = words[ i ].match.length + lineLength > maxChars;

      if ( willOverflow )
      {
        label.push( "-br-" );
        lineLength = 0;
      }
     
      label.push( words[ i ].match );
      lineLength += words[ i ].match.length;
      
    }
    */

    let combinedLabel = aligner.contentLines.join( "-br-" );

    let anchored = ElementType.anchor.create( combinedLabel );

    let index = GodotDocsIndexSelector.currentIndex;

    let nodeReference = index.index.get( i._type );

    console.log( "Grabbing from index:", i._type, nodeReference );

    if ( ! nodeReference && i._type.endsWith( "s" ) )
    { 
      let singularType = i._type.replace( /s$/, "" ); 
      let singularReference = index.index.get( singularType );
      let typeBefore = i._type;

      if ( ! singularReference && i._type.endsWith( "ies" ) )
      {
        singularType = i._type.replace( /ies$/, "y" ); 
        singularReference = index.index.get( singularType );
      }
      
      if ( singularReference )
      {
        i._type = singularType;
      }

      console.log( "Grabbing from index again:", typeBefore, i._name, i._type, nodeReference );
    }

    if ( nodeReference && i._type != "Node")
    {
      let link = GodotDocsFile.getLink( nodeReference );
      // console.log( "ref for:", i._type, link );
      ElementAttribute.href.to( anchored, link );
      new ElementAttribute( "root-link" ).to( anchored, link );
      // Rokojori.$.processRawTemplateElement( anchored );
      
    }

    let nodeElement = GodotNode.type.create( GodotNode.label.create( anchored ) );
    let icon = GodotIcons.getNodeIconForClass( i.image );

    // console.log( "image:", i.image, " >> icon:", icon );
    GodotNode.iconAttribute.to( nodeElement, icon );    

    // console.log( nodeElement.outerHTML );
    i._element = Rokojori.$.processRawTemplateElement( nodeElement ) as HTMLElement;
    i._element.innerHTML = i._element.innerHTML.replace( /-br-/g, "<portrait-br></portrait-br><portrait-only>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</portrait-only>" );
    i._element.style.left = `calc( ${i._offset} * 0.5em )` ;
    
    i._labelElement = GodotNode.label.query( i._element );
    i._iconElement = GodotNode.nodeIcon.query( i._element );
    

    ElementAttribute.title.to( i._element, `${i.label}\nType: ${i._type}` );

    OnClick.add( GodotNode.open.query( i._element ),       
      () => i.setChildrenVisibility( false )
    );

    OnClick.add( GodotNode.closed.query( i._element ),       
      () => i.setChildrenVisibility( true )
    );

    OnClick.add( GodotNode.label.query( i._element ), 
      () => i.toggleChildrenVisibility()
    )

    return i;
  }

}